import { normalizePlainText } from './user-text';
import { FEED_POST_MAX_LENGTH, photoRatio, toFeedLink } from './feed-post';
import { toPartner } from './feed-partner';
import { ANNOUNCEMENT_MAX_LENGTH, announcementLive, validAnnouncementId } from './club-announcement';
import { officialImage } from './official-content';

// Lig akışı yardımcıları (saf). Olayları tenis.io-functions `league-feed-service` yazar;
// web yalnız okur, doğrular, gün gün gruplar. Sözleşme: docs/LEAGUE-FEED.md
export const FEED_PAGE_SIZE = 20;

/**
 * Akış cursor'u `<olusturma ms>_<belge kimliği>`. Aynı milisaniyedeki olaylar kimlikle
 * ayrışır; sorgu `olusturma desc, __name__ desc` sırasıyla aynı sırayı izler.
 */
export type FeedCursor = { olusturma: number; id: string };

const CURSOR_PATTERN = /^(\d{1,16})_([A-Za-z0-9_-]{1,128})$/;

export const encodeFeedCursor = (cursor: FeedCursor) => `${cursor.olusturma}_${cursor.id}`;

/** Geçersiz cursor `null`; çağıran bunu 400 olarak yanıtlar. */
export function parseFeedCursor(value: unknown): FeedCursor | null {
  if (typeof value !== 'string') return null;
  const match = CURSOR_PATTERN.exec(value);
  if (!match) return null;
  const olusturma = Number(match[1]);
  return olusturma > 0 && Number.isSafeInteger(olusturma) ? { olusturma, id: match[2] } : null;
}

/** Sunucu sırasıyla aynı: yeniden eskiye, eşit zamanda kimlik azalan. */
export const compareFeedEvents = (a: FeedCursor, b: FeedCursor) =>
  b.olusturma - a.olusturma || (a.id < b.id ? 1 : a.id > b.id ? -1 : 0);

// LG-69: arada başka kart yoksa aynı haftanın 2+ katılımı tek kartta (önceden aynı gün 3+).
export const JOIN_GROUP_THRESHOLD = 2;

const TYPES: LeagueFeed.Type[] = [
  'katildi',
  'mac_sonucu',
  'kilometre_tasi',
  'haftalik_ozet',
  'ayin_aktifleri',
  'sezon',
  'kulup',
  'turnuva',
  'gonderi',
  'duyuru',
];

const CLUB_EVENTS: LeagueFeed.ClubEvent[] = ['yeni_lig', 'yeni_kort', 'yeni_antrenor'];
const TOURNAMENT_EVENTS: LeagueFeed.TournamentEvent[] = [
  'kayit_acildi',
  'kura_yayinlandi',
  'tamamlandi',
];

const toRef = (value: any): LeagueFeed.Ref | undefined =>
  value &&
  typeof value.id === 'string' &&
  /^[A-Za-z0-9_-]{1,128}$/.test(value.id) &&
  asString(value.ad).trim()
    ? { id: value.id, ad: asString(value.ad).trim() }
    : undefined;

const asString = (value: unknown) => (typeof value === 'string' ? value : '');

const toPlayer = (value: any): LeagueFeed.Player | undefined => {
  if (!value || typeof value.id !== 'string' || !value.id) return undefined;
  return {
    id: value.id,
    ad: asString(value.ad),
    soyad: asString(value.soyad),
    foto_mini_url: asString(value.foto_mini_url) || null,
    null_seviye: asString(value.null_seviye) || null,
    verified_account: value.verified_account === true,
  };
};

const toNumber = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

// B3: bozuk kayıt gösterilmez (iki oyuncu ve geçerli kart kimliği şart).
const toWeeklyMatch = (value: any): LeagueFeed.Event['veri']['haftanin_maci'] => {
  const kazanan = toPlayer(value?.kazanan);
  const kaybeden = toPlayer(value?.kaybeden);
  const count = toNumber(value?.tebrik_sayisi);
  if (
    !kazanan ||
    !kaybeden ||
    !count ||
    typeof value.olay_id !== 'string' ||
    !/^[A-Za-z0-9_-]{1,160}$/.test(value.olay_id)
  )
    return undefined;
  return {
    olay_id: value.olay_id,
    kazanan,
    kaybeden,
    setler: Array.isArray(value.setler)
      ? value.setler
          .filter((set: any) => set && typeof set === 'object')
          .map((set: any) => ({
            kazanan: Number(set.kazanan) || 0,
            kaybeden: Number(set.kaybeden) || 0,
          }))
      : [],
    tebrik_sayisi: count,
  };
};

const toReactors = (value: unknown): LeagueFeed.Reactor[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const list = value
    .map(item => toPlayer(item))
    .filter((item): item is LeagueFeed.Player => !!item)
    .slice(0, 3)
    .map(player => ({
      id: player.id,
      ad: player.ad,
      soyad: player.soyad,
      foto_mini_url: player.foto_mini_url,
    }));
  return list.length ? list : undefined;
};

const toReactionCounts = (value: any): LeagueFeed.Event['tepki_sayilari'] => {
  if (!value || typeof value !== 'object') return undefined;
  const counts: NonNullable<LeagueFeed.Event['tepki_sayilari']> = {};
  (['alkis', 'ates', 'top'] as const).forEach(kind => {
    const count = Number(value[kind]);
    if (Number.isInteger(count) && count > 0) counts[kind] = count;
  });
  return Object.keys(counts).length ? counts : undefined;
};

const STORIES = ['rovans', 'seri', 'geri_donus', 'tiebreak'];
const toStory = (value: any): LeagueFeed.Story | undefined => {
  if (!value || !STORIES.includes(value.tur)) return undefined;
  if (value.tur !== 'seri') return { tur: value.tur };
  const count = Number(value.sayi);
  return Number.isInteger(count) && count >= 3 && count <= 1000
    ? { tur: 'seri', sayi: count }
    : undefined;
};

// Ligdeki ikili karşılaşma (bu maç dahil): yalnız en az iki maç oynanmışsa anlamlı.
const toHeadToHead = (value: any): LeagueFeed.HeadToHead | undefined => {
  const winner = Number(value?.kazanan);
  const loser = Number(value?.kaybeden);
  if (!Number.isInteger(winner) || !Number.isInteger(loser) || winner < 1 || loser < 0)
    return undefined;
  return winner + loser >= 2 && winner + loser <= 1000
    ? { kazanan: winner, kaybeden: loser }
    : undefined;
};

/** Firestore verisini serileştirilebilir ve doğrulanmış bir olaya çevirir; bozuk kayıt null. */
function toAnnouncement(value: any): LeagueFeed.Event['veri']['duyuru'] {
  if (!value || typeof value !== 'object' || !validAnnouncementId(value.id)) return undefined;
  const metin = normalizePlainText(value.metin, ANNOUNCEMENT_MAX_LENGTH);
  const yayin = toNumber(value.yayin);
  const bitis = toNumber(value.bitis);
  if (!metin || !yayin || !bitis || bitis <= yayin) return undefined;
  return {
    id: value.id,
    metin,
    baglanti: toFeedLink(value.baglanti),
    ...(officialImage(value.gorsel) ? { gorsel: value.gorsel as string } : {}),
    yayin,
    bitis,
    revizyon: toNumber(value.revizyon) || 1,
  };
}

function toAnnouncementAuthor(value: any): LeagueFeed.Event['veri']['yazan'] {
  if (!value || typeof value !== 'object' || typeof value.id !== 'string' || !value.id) return undefined;
  if (!['admin', 'bas', 'yardimci', 'tenisio'].includes(value.rol)) return undefined;
  return {
    id: value.id,
    ad: asString(value.ad),
    soyad: asString(value.soyad),
    foto_mini_url: typeof value.foto_mini_url === 'string' ? value.foto_mini_url : null,
    rol: value.rol,
  };
}

export function normalizeFeedEvent(id: string, data: any, now = Date.now()): LeagueFeed.Event | null {
  if (!data || !TYPES.includes(data.tur)) return null;
  // Şikâyetle gizlenen kullanıcı gönderisi admin bakana kadar hiçbir akışta görünmez.
  if (data.gizli === true) return null;
  const olusturma = toNumber(data.olusturma);
  if (!olusturma) return null;
  const veri = data.veri || {};
  const players = (list: unknown, keys: string[]): any[] | undefined =>
    Array.isArray(list)
      ? list
          .filter(item => toPlayer(item?.oyuncu))
          .map(item => ({
            oyuncu: toPlayer(item.oyuncu),
            ...Object.fromEntries(keys.map(key => [key, toNumber(item[key]) || 0])),
          }))
      : undefined;
  const event: LeagueFeed.Event = {
    id,
    tur: data.tur,
    olusturma,
    tebrik_sayisi: toNumber(data.tebrik_sayisi) || 0,
    tebrik_edenler: Array.isArray(data.tebrik_edenler)
      ? data.tebrik_edenler.filter((item: unknown) => typeof item === 'string')
      : [],
    tebrik_onizleme: toReactors(data.tebrik_onizleme),
    tepki_sayilari: toReactionCounts(data.tepki_sayilari),
    veri: {
      oyuncu: toPlayer(veri.oyuncu),
      kazanan: toPlayer(veri.kazanan),
      kaybeden: toPlayer(veri.kaybeden),
      setler: Array.isArray(veri.setler)
        ? veri.setler
            .filter((set: any) => set && typeof set === 'object')
            .map((set: any) => ({
              kazanan: Number(set.kazanan) || 0,
              kaybeden: Number(set.kaybeden) || 0,
            }))
        : undefined,
      puan: toNumber(veri.puan) ?? null,
      mac_id:
        typeof veri.mac_id === 'string' && /^[A-Za-z0-9_-]{1,128}$/.test(veri.mac_id)
          ? veri.mac_id
          : undefined,
      mac_saati: toNumber(veri.mac_saati) || undefined,
      mac_foto_var_mi: veri.mac_foto_var_mi === true ? true : undefined,
      mac_foto_oran: veri.mac_foto_oran === undefined ? undefined : photoRatio(veri.mac_foto_oran),
      hikaye: toStory(veri.hikaye),
      ilk_mac: Array.isArray(veri.ilk_mac)
        ? veri.ilk_mac
            .filter(
              (item: unknown) =>
                typeof item === 'string' && [veri.kazanan?.id, veri.kaybeden?.id].includes(item),
            )
            .slice(0, 2)
        : undefined,
      ikili: toHeadToHead(veri.ikili),
      tip: veri.tip === 'ilk_galibiyet' || veri.tip === 'mac_sayisi' ? veri.tip : undefined,
      sayi: toNumber(veri.sayi),
      hafta: asString(veri.hafta) || undefined,
      mac_sayisi: toNumber(veri.mac_sayisi),
      yeni_oyuncu_sayisi: toNumber(veri.yeni_oyuncu_sayisi),
      verilen_tebrik: toNumber(veri.verilen_tebrik),
      yukselenler: players(veri.yukselenler, ['fark', 'sira']),
      haftanin_maci: toWeeklyMatch(veri.haftanin_maci),
      ay: asString(veri.ay) || undefined,
      oyuncular: players(veri.oyuncular, ['mac_sayisi']),
      durum: veri.durum === 'basladi' || veri.durum === 'bitiyor' ? veri.durum : undefined,
      bitis: toNumber(veri.bitis) ?? null,
      kalan_gun: toNumber(veri.kalan_gun),
      olay: (data.tur === 'turnuva'
        ? (TOURNAMENT_EVENTS as string[])
        : (CLUB_EVENTS as string[])
      ).includes(veri.olay)
        ? veri.olay
        : undefined,
      turnuva: toRef(veri.turnuva),
      bas_tarih: data.tur === 'turnuva' ? toNumber(veri.bas_tarih) ?? null : undefined,
      basvuru_bitis: data.tur === 'turnuva' ? toNumber(veri.basvuru_bitis) ?? null : undefined,
      sampiyonlar:
        data.tur === 'turnuva' && Array.isArray(veri.sampiyonlar)
          ? veri.sampiyonlar
              .filter((item: any) => item && asString(item.ad).trim())
              .slice(0, 6)
              .map((item: any) => ({
                kategori: asString(item.kategori).trim() || null,
                ad: asString(item.ad).trim(),
              }))
          : undefined,
      klup: toRef(veri.klup),
      lig: toRef(veri.lig),
      kort: toRef(veri.kort),
      antrenor: toPlayer(veri.antrenor),
      rol: veri.rol === 'bas' || veri.rol === 'yardimci' ? veri.rol : undefined,
      metin:
        data.tur === 'gonderi'
          ? normalizePlainText(veri.metin, FEED_POST_MAX_LENGTH) || undefined
          : undefined,
      foto_var_mi: data.tur === 'gonderi' && veri.foto_var_mi === true ? true : undefined,
      foto_oran: data.tur === 'gonderi' ? photoRatio(veri.foto_oran) : undefined,
      baglanti: data.tur === 'gonderi' ? toFeedLink(veri.baglanti) : undefined,
      partner: data.tur === 'gonderi' ? toPartner(veri.partner) : undefined,
      duyuru: data.tur === 'duyuru' ? toAnnouncement(veri.duyuru) : undefined,
      yazan: data.tur === 'duyuru' ? toAnnouncementAuthor(veri.yazan) : undefined,
    },
  };
  // LG-61: duyuru yalnız yayın ile bitiş arasında görünür.
  if (data.tur === 'duyuru' && (!event.veri.duyuru || !announcementLive(event.veri.duyuru.yayin, event.veri.duyuru.bitis, now))) {
    return null;
  }
  // Kartı çizilemeyecek olay gösterilmez.
  const { veri: v } = event;
  const drawable = {
    katildi: !!v.oyuncu,
    mac_sonucu: !!(v.kazanan && v.kaybeden),
    kilometre_tasi: !!(v.oyuncu && v.tip),
    haftalik_ozet: !!v.hafta,
    ayin_aktifleri: !!v.oyuncular?.length,
    sezon: !!v.durum,
    kulup:
      !!v.klup &&
      ((v.olay === 'yeni_lig' && !!v.lig) ||
        (v.olay === 'yeni_kort' && !!v.kort) ||
        (v.olay === 'yeni_antrenor' && !!v.antrenor)),
    turnuva: !!v.olay && !!v.turnuva,
    gonderi: !!v.oyuncu && !!(v.metin || v.foto_var_mi || v.baglanti || v.partner),
    // Kulüp duyurusu kulübüyle; tenis.io resmi içeriği kulüpsüz (yazan rolü `tenisio`).
    duyuru: !!v.duyuru && !!v.yazan && (!!v.klup || v.yazan.rol === 'tenisio'),
  }[event.tur];
  if (!drawable) return null;
  // undefined alanlar Next props serileştirmesini bozar.
  return JSON.parse(JSON.stringify(event));
}

/** İstanbul takvim günü, `YYYY-MM-DD` (sunucu ve tarayıcı aynı günü üretir). */
export function feedDay(ms: number): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Istanbul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(ms));
}

/** İstanbul haftasının pazartesi günü, `YYYY-MM-DD`. */
export function feedWeek(ms: number): string {
  const day = Date.parse(`${feedDay(ms)}T00:00:00Z`);
  const weekday = (new Date(day).getUTCDay() + 6) % 7;
  return new Date(day - weekday * 86400000).toISOString().slice(0, 10);
}

/**
 * Ham olayları koruyarak ardışık katılımları (aynı lig ve hafta, arada başka kart yok) ve kimliği
 * bilinen maçın türevlerini toplar. Bağlamı eksik ana sayfa olayları birbirine karıştırılmaz.
 */
export function groupFeed(
  events: LeagueFeed.Event[],
  groupJoins = true,
  context?: Record<string, LeagueFeed.HomeContext>,
): LeagueFeed.Section[] {
  const sorted = Array.from(new Map(events.map(event => [event.id, event])).values()).sort(
    compareFeedEvents,
  );
  const league = (event: LeagueFeed.Event) => (context ? context[event.id]?.league.id : 'league');
  const buckets = new Map<string, LeagueFeed.Event[]>();
  // Katılım dizisi: sıralı akışta aynı lig ve haftanın kesintisiz katılımları. Kimlik en eski olaya
  // bağlı; yeni katılım eklenince kart kimliği (konum geri yükleme) değişmez.
  let run: { owner: string; week: string; events: LeagueFeed.Event[] } | null = null;
  const closeRun = () => {
    if (run)
      buckets.set(
        `joins_${run.week}_${run.owner}_${run.events[run.events.length - 1].id}`,
        run.events,
      );
    run = null;
  };
  sorted.forEach(event => {
    const owner = league(event);
    if (groupJoins && owner && event.tur === 'katildi' && event.veri.oyuncu?.id) {
      const week = feedWeek(event.olusturma);
      if (run && run.owner === owner && run.week === week) run.events.push(event);
      else {
        closeRun();
        run = { owner, week, events: [event] };
      }
      return;
    }
    closeRun();
    if (!owner) return;
    if (event.veri.mac_id && ['mac_sonucu', 'kilometre_tasi'].includes(event.tur)) {
      const key = `match_${owner.length}_${owner}_${event.veri.mac_id}`;
      buckets.set(key, [...(buckets.get(key) || []), event]);
    }
  });
  closeRun();
  const groups = new Map<string, LeagueFeed.Item>();
  buckets.forEach((list, key) => {
    if (key.startsWith('joins_')) {
      const players = Array.from(
        new Map(list.map(event => [event.veri.oyuncu!.id, event.veri.oyuncu!])).values(),
      );
      if (players.length < JOIN_GROUP_THRESHOLD) return;
      const item: LeagueFeed.Item = {
        kind: 'joins',
        id: key,
        olusturma: list[0].olusturma,
        players,
        events: list,
      };
      list.forEach(event => groups.set(event.id, item));
    } else {
      const results = list.filter(event => event.tur === 'mac_sonucu');
      // Şüpheli çoğul sonuçları veya başka oyuncunun kilometre taşını birleştirme.
      if (results.length !== 1) return;
      const result = results[0];
      const subjects = [result.veri.kazanan?.id, result.veri.kaybeden?.id];
      const related = list.filter(
        event => event.tur === 'kilometre_tasi' && subjects.includes(event.veri.oyuncu?.id),
      );
      if (!related.length) return;
      const item: LeagueFeed.Item = {
        kind: 'match',
        id: key,
        olusturma: Math.max(result.olusturma, ...related.map(event => event.olusturma)),
        event: result,
        related,
      };
      [result, ...related].forEach(event => groups.set(event.id, item));
    }
  });
  const emitted = new Set<string>();
  return sorted.reduce<LeagueFeed.Section[]>((sections, event) => {
    const item = groups.get(event.id) || { kind: 'event' as const, event };
    const id = item.kind === 'event' ? event.id : item.id;
    if (emitted.has(id)) return sections;
    emitted.add(id);
    const day = feedDay(item.kind === 'event' ? event.olusturma : item.olusturma);
    const last = sections[sections.length - 1];
    if (last?.day === day) last.items.push(item);
    else sections.push({ day, items: [item] });
    return sections;
  }, []);
}

export const feedItemEvents = (item: LeagueFeed.Item): LeagueFeed.Event[] =>
  item.kind === 'event'
    ? [item.event]
    : item.kind === 'joins'
    ? item.events
    : [item.event, ...item.related];

/** "Bugün" / "Dün" / tarih ayrımı için: 0 bugün, 1 dün, diğerleri tarih. */
export function daysAgo(day: string, now: number): number {
  const today = Date.parse(`${feedDay(now)}T00:00:00Z`);
  return Math.round((today - Date.parse(`${day}T00:00:00Z`)) / 86400000);
}

export const formatSets = (sets: { kazanan: number; kaybeden: number }[] = []) =>
  sets.map(set => `${set.kazanan}-${set.kaybeden}`).join(' ');

export const playerName = (player?: LeagueFeed.Player | null) =>
  player ? `${player.ad} ${player.soyad}`.trim() : '';

/** Sayfalama birleşimi: aynı kimlik iki kez gelmez, sıralama korunur. */
export function mergeFeedPages(current: LeagueFeed.Event[], next: LeagueFeed.Event[]) {
  const seen = new Set(current.map(event => event.id));
  return [...current, ...next.filter(event => !seen.has(event.id))].sort(compareFeedEvents);
}

/** İyimser tebrik: sunucu sayacı tetikleyiciyle gelene kadar yerel olarak uygulanır. */
export function applyCongrats(
  event: LeagueFeed.Event,
  viewerId: string,
  congratulated: boolean,
): LeagueFeed.Event {
  const has = event.tebrik_edenler.includes(viewerId);
  if (has === congratulated) return event;
  return {
    ...event,
    tebrik_sayisi: Math.max(0, event.tebrik_sayisi + (congratulated ? 1 : -1)),
    tebrik_edenler: congratulated
      ? [...event.tebrik_edenler, viewerId]
      : event.tebrik_edenler.filter(id => id !== viewerId),
  };
}

// Cümle içindeki tıklanabilir öğeler (oyuncu adı vb.) çeviriye bu işaretle girer, sonra bölünür.
// Böylece her dilde ad, cümledeki doğal yerinde kalır ve Türkçede ada ek gerekmez.
export const sentenceToken = (name: string) => `⟦${name}⟧`;

export function splitSentence(text: string): (string | { token: string })[] {
  return text
    .split(/⟦(\w+)⟧/)
    .map((part, index) => (index % 2 ? { token: part } : part))
    .filter(part => part !== '');
}

/**
 * Kartı tebrik edemeyen oyuncular (kartın öznesi). Maç kartında yalnız kazanan: kaybeden rakibini
 * "İyi maçtı" diye tebrik edebilir (3 Kasım 2026, akış araştırması Ç2). Sunucu ve arayüz aynı kuralı kullanır.
 */
export const feedSubjectIds = (event: { tur: string; veri?: any }): string[] =>
  (event.tur === 'mac_sonucu'
    ? [event.veri?.kazanan?.id]
    : [event.veri?.oyuncu?.id, event.veri?.kazanan?.id, event.veri?.kaybeden?.id]
  ).filter((id): id is string => typeof id === 'string' && !!id);

/** Maç kartını kaybeden oyuncu görüyor: tebrik düğmesi "İyi maçtı" olur. */
export const isFeedRival = (event: { tur: string; veri?: any }, playerId?: string | null) =>
  event.tur === 'mac_sonucu' && !!playerId && event.veri?.kaybeden?.id === playerId;

const ISTANBUL_OFFSET = 3 * 60 * 60 * 1000;
const NIGHT_START_HOUR = 22;
const NIGHT_END_HOUR = 6;
const MORNING_END_HOUR = 12;

/**
 * Gece (22:00–06:00) onaylanan maç kartı ertesi gün 12:00'ye kadar akışın başında kalır
 * (Ç4: gece doğan kartın 24 saatte tebrik alma oranı düşük). Saatler İstanbul (UTC+3).
 */
export function isLastNightMatch(event: { tur: string; olusturma: number }, now: number): boolean {
  if (event.tur !== 'mac_sonucu') return false;
  const local = now + ISTANBUL_OFFSET;
  const hour = new Date(local).getUTCHours();
  if (hour >= MORNING_END_HOUR) return false;
  const midnight = local - (local % (24 * 60 * 60 * 1000)) - ISTANBUL_OFFSET;
  const start = midnight - (24 - NIGHT_START_HOUR) * 60 * 60 * 1000;
  const end = midnight + NIGHT_END_HOUR * 60 * 60 * 1000;
  return event.olusturma >= start && event.olusturma < end && event.olusturma <= now;
}

/** Ç4: bu sabah için "Dün gece" kartları (en çok 3, yeniden eskiye) başa alınır; sıra başka değişmez. */
export function pinLastNight(
  items: LeagueFeed.Item[],
  now: number,
): { items: LeagueFeed.Item[]; pinned: Set<string> } {
  const primary = (item: LeagueFeed.Item) => (item.kind === 'joins' ? null : item.event);
  const pinned = items
    .filter(item => {
      const event = primary(item);
      return !!event && isLastNightMatch(event, now);
    })
    .slice(0, 3);
  if (!pinned.length) return { items, pinned: new Set() };
  return {
    items: [...pinned, ...items.filter(item => !pinned.includes(item))],
    pinned: new Set(pinned.map(item => primary(item)!.id)),
  };
}

const POPULAR_MIN_CONGRATS = 3;
const POPULAR_WINDOW_MS = 48 * 60 * 60 * 1000;
// Yalnız ilk sayfa kadar kartta aranır: sonraki sayfa yüklenince okunan yerin üstüne kart taşınmasın.
const POPULAR_SCAN_ITEMS = 20;
const POPULAR_TYPES = ['katildi', 'mac_sonucu', 'kilometre_tasi', 'gonderi'];

/**
 * Çok tebrik alan kart (5 Kas): son 48 saatte en az 3 tebrik almış, ilk 3 sırada olmayan en çok
 * tebrikli tek kart "Dün gece" kartlarının hemen altına alınır; etiket yok. Sayı sunucudaki
 * `tebrik_sayisi`dir (sayfadaki canlı tebrik sırayı oynatmaz). Başka sıra değişmez.
 */
export function liftPopular(
  items: LeagueFeed.Item[],
  now: number,
  after = 0,
): { items: LeagueFeed.Item[]; lifted: string | null } {
  const primary = (item: LeagueFeed.Item) => (item.kind === 'joins' ? null : item.event);
  const target = items
    .slice(0, POPULAR_SCAN_ITEMS)
    .map((item, index) => ({ item, index, event: primary(item) }))
    .filter(
      ({ index, event }) =>
        index >= after + 3 &&
        !!event &&
        POPULAR_TYPES.includes(event.tur) &&
        event.olusturma <= now &&
        now - event.olusturma <= POPULAR_WINDOW_MS &&
        event.tebrik_sayisi >= POPULAR_MIN_CONGRATS,
    )
    .sort(
      (a, b) =>
        b.event!.tebrik_sayisi - a.event!.tebrik_sayisi || b.event!.olusturma - a.event!.olusturma,
    )[0];
  if (!target) return { items, lifted: null };
  const rest = items.filter(item => item !== target.item);
  return {
    items: [...rest.slice(0, after), target.item, ...rest.slice(after)],
    lifted: target.event!.id,
  };
}

const RANK_WINDOW_MS = 72 * 60 * 60 * 1000;
const RANK_HALF_LIFE_MS = 24 * 60 * 60 * 1000;
const RANK_POST_HALF_LIFE_MS = 48 * 60 * 60 * 1000;
// İlk 5 kartta aynı kişiden en çok bir kart ve (varsa) en az bir keşif kartı (yeni oyuncu/ilk maç).
const RANK_TOP = 5;
// S3: bu tarayıcıda daha önce görülen kartın puanı yarıya iner (gizlenmez).
const RANK_SEEN_FACTOR = 0.5;

/**
 * S1 kişiye özel sıralama (5 Kas, ana sayfa): son 72 saatin ilk sayfadaki kartları
 * `(1 + ilgi + popülerlik) × tazelik` puanıyla dizilir; daha eskiler zaman sırasında kalır. `ilgi`
 * sunucudan gelir (`HomeContext.ilgi`); bağ olmayınca sıra zaman sırasıdır. Kart gizlenmez,
 * gerekçe gösterilmez. `liftPopular` kuralının ana sayfadaki karşılığıdır. S3: `seen` (bu
 * tarayıcıda daha önce görülen kartlar) puanı yarıya indirir.
 */
export function rankForViewer(
  items: LeagueFeed.Item[],
  now: number,
  context: Record<string, LeagueFeed.HomeContext> = {},
  after = 0,
  seen: Set<string> = new Set(),
): LeagueFeed.Item[] {
  const events = (item: LeagueFeed.Item) =>
    item.kind === 'joins' ? item.events : [item.event, ...(item.kind === 'match' ? item.related : [])];
  const time = (item: LeagueFeed.Item) => (item.kind === 'event' ? item.event.olusturma : item.olusturma);
  let end = after;
  while (
    end < Math.min(items.length, POPULAR_SCAN_ITEMS) &&
    time(items[end]) <= now &&
    now - time(items[end]) <= RANK_WINDOW_MS
  ) end += 1;
  if (end - after < 2) return items;
  const scored = items.slice(after, end).map(item => {
    const primary = item.kind === 'joins' ? null : item.event;
    const interest = Math.max(0, ...events(item).map(event => context[event.id]?.ilgi || 0));
    const popular =
      !!primary &&
      POPULAR_TYPES.includes(primary.tur) &&
      now - primary.olusturma <= POPULAR_WINDOW_MS &&
      primary.tebrik_sayisi >= POPULAR_MIN_CONGRATS;
    const halfLife = primary?.tur === 'gonderi' ? RANK_POST_HALF_LIFE_MS : RANK_HALF_LIFE_MS;
    return {
      item,
      subject: primary ? feedSubjectIds(primary)[0] || null : null,
      explore: item.kind === 'joins' || primary?.tur === 'katildi' || !!primary?.veri.ilk_mac?.length,
      score:
        (1 + interest + (popular ? 1 : 0)) *
        0.5 ** ((now - time(item)) / halfLife) *
        (events(item).some(event => seen.has(event.id)) ? RANK_SEEN_FACTOR : 1),
    };
  });
  const pool = scored.sort((a, b) => b.score - a.score || time(b.item) - time(a.item));
  const ranked: typeof pool = [];
  const subjects = new Set<string>();
  while (pool.length) {
    let index = 0;
    if (ranked.length < RANK_TOP) {
      const free = pool.findIndex(entry => !entry.subject || !subjects.has(entry.subject));
      const explore = pool.findIndex(entry => entry.explore);
      if (ranked.length === RANK_TOP - 1 && !ranked.some(entry => entry.explore) && explore >= 0) {
        index = explore;
      } else if (free >= 0) index = free;
    }
    const [entry] = pool.splice(index, 1);
    if (entry.subject) subjects.add(entry.subject);
    ranked.push(entry);
  }
  return [...items.slice(0, after), ...ranked.map(entry => entry.item), ...items.slice(end)];
}

/** C3: sıralamada yakınlık eşiği (Aral & Nicolaides 2017: etki benzer düzeydekiler arasında güçlü). */
export const NEAR_RANK_RANGE = 3;

/** İki oyuncu sıralamada ±3 içinde mi (sırasız ya da aynı oyuncu değil). */
export const isNearRank = (viewerRank?: number | null, playerRank?: number | null) =>
  Number.isInteger(viewerRank) &&
  Number.isInteger(playerRank) &&
  (viewerRank as number) > 0 &&
  (playerRank as number) > 0 &&
  viewerRank !== playerRank &&
  Math.abs((viewerRank as number) - (playerRank as number)) <= NEAR_RANK_RANGE;
